/**
 * Agents — promoters who earn a recurring share of the house commission the
 * players they referred generate (migration 070).
 *
 * **This screen does not move money.** Earnings accrue per settled round and
 * sit here as a debt; an admin sends the money over Telebirr by hand and then
 * presses "Mark paid" to record that they did. The button therefore reports a
 * payment rather than making one, which is why it names the figure and why
 * the server refuses it if the balance has changed since this list loaded —
 * a round that settled in between must not be quietly marked as sent.
 *
 * Making someone an agent happens on their player page, not here: you find a
 * person by looking them up, and a screen that lists only existing agents is
 * the wrong place to search for someone who is not one yet.
 */

import { useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { etb, parseAmount } from '../format.ts';
import { can } from '../auth.ts';
import { ConfirmButton, ErrorBanner, Field, Spinner } from '../components/ui.tsx';
import { href } from '../router.ts';
import type { AgentView, AgentBotView, AgentCollectionView, AdminView } from '../api/types.ts';

/** Basis points as a percentage, without trailing noise: 5000 -> "50%". */
function pct(bps: number): string {
  return `${String(Math.round(bps / 100 * 100) / 100)}%`;
}

export function Agents({ me }: { me: AdminView }) {
  const [agents, setAgents] = useState<AgentView[] | null>(null);
  const [bots, setBots] = useState<AgentBotView[] | null>(null);
  const [collections, setCollections] = useState<AgentCollectionView[] | null>(null);
  const [remit, setRemit] = useState<Record<string, string>>({});
  const [collectionForm, setCollectionForm] = useState({
    agentId: '',
    telebirrAccount: '',
    accountName: '',
    smsWebhookSecret: '',
    dailyCreditCap: '',
    floatCeiling: '',
  });
  const [form, setForm] = useState({
    agentId: '',
    slug: '',
    botToken: '',
    botUsername: '',
    miniAppName: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const manage = can(me.role, 'agent.manage');

  const load = async (): Promise<void> => {
    setError(null);
    try {
      const [list, botList, collectionList] = await Promise.all([
        api.agents(),
        api.agentBots(),
        api.agentCollections(),
      ]);
      setAgents(list);
      setBots(botList);
      setCollections(collectionList);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function markPaid(agent: AgentView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.markAgentPaid(agent.userId, agent.owed);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function saveBot(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.saveAgentBot({
        agentId: form.agentId.trim(),
        slug: form.slug.trim(),
        botToken: form.botToken.trim(),
        botUsername: form.botUsername.trim(),
        ...(form.miniAppName.trim() ? { miniAppName: form.miniAppName.trim() } : {}),
      });
      setForm({ agentId: '', slug: '', botToken: '', botUsername: '', miniAppName: '' });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function saveCollection(): Promise<void> {
    const cap = parseAmount(collectionForm.dailyCreditCap.trim());
    const ceiling = parseAmount(collectionForm.floatCeiling.trim());
    if (!cap.ok || !ceiling.ok) {
      setError(new Error('Enter both limits in ETB, for example 5000'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.saveAgentCollection({
        agentId: collectionForm.agentId.trim(),
        telebirrAccount: collectionForm.telebirrAccount.trim(),
        smsWebhookSecret: collectionForm.smsWebhookSecret.trim(),
        dailyCreditCap: cap.santim,
        floatCeiling: ceiling.santim,
        ...(collectionForm.accountName.trim() ? { accountName: collectionForm.accountName.trim() } : {}),
      });
      setCollectionForm({
        agentId: '',
        telebirrAccount: '',
        accountName: '',
        smsWebhookSecret: '',
        dailyCreditCap: '',
        floatCeiling: '',
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function recordRemittance(c: AgentCollectionView): Promise<void> {
    const amount = parseAmount((remit[c.agentId] ?? '').trim());
    if (!amount.ok) {
      setError(new Error('Enter the amount they sent, in ETB'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.remitAgentFloat(c.agentId, amount.santim);
      setRemit({ ...remit, [c.agentId]: '' });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggleFrozen(c: AgentCollectionView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.setAgentFrozen(c.agentId, !c.frozen);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggleBot(bot: AgentBotView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.setAgentBotActive(bot.agentId, !bot.active);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function stop(agent: AgentView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.setAgentRate(agent.userId, null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <header className="pagehead">
        <h1>Agents</h1>
      </header>

      <ErrorBanner error={error} />
      {agents === null ? <Spinner /> : null}

      {agents ? (
        <div className="card">
          <p className="muted">
            Earnings accrue automatically as rounds settle. Pay an agent over Telebirr yourself,
            then press Mark paid to record it — nothing here sends money. This is money the house
            owes <em>them</em>; what they owe the house is their collection float, below.
          </p>
          {agents.length === 0 ? (
            <p className="muted">
              No agents yet. Open a player's page and set an agent rate to make them one.
            </p>
          ) : (
            <table className="table" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Agent</th>
                  <th scope="col" role="columnheader">Rate</th>
                  <th scope="col" role="columnheader">Players</th>
                  <th scope="col" role="columnheader">Commission owed</th>
                  <th scope="col" role="columnheader">Lifetime</th>
                  <th scope="col" role="columnheader" />
                </tr>
              </thead>
              <tbody role="rowgroup">
                {agents.map((a) => (
                  <tr role="row" key={a.userId}>
                    <td role="cell" data-label="Agent">
                      <a href={href({ name: 'player', id: a.userId })}>
                        {a.fullName ?? (a.username ? `@${a.username}` : a.phone ?? 'unnamed')}
                      </a>
                    </td>
                    <td role="cell" data-label="Rate">{pct(a.rateBps)}</td>
                    <td role="cell" data-label="Players" className="muted">{a.referredPlayers}</td>
                    <td role="cell" data-label="Commission owed">{etb(a.owed)}</td>
                    <td role="cell" data-label="Lifetime" className="muted">{etb(a.lifetime)}</td>
                    <td role="cell" data-label="">
                      {manage ? (
                        <>
                          {a.owed === '0' ? null : (
                            <ConfirmButton
                              className="button button--primary"
                              confirm={`Record that ${etb(a.owed)} has been sent to this agent? Do this only after the money has actually left.`}
                              disabled={busy}
                              onConfirm={() => void markPaid(a)}
                            >
                              Mark paid
                            </ConfirmButton>
                          )}
                          <ConfirmButton
                            className="button button--danger"
                            confirm="Stop this person being an agent? They keep anything already earned, but future rounds accrue nothing."
                            disabled={busy}
                            onConfirm={() => void stop(a)}
                          >
                            Stop
                          </ConfirmButton>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {collections && collections.length > 0 ? (
        <div className="card">
          <h2>Collection floats</h2>
          <p className="muted">
            What each agent has collected into their own Telebirr and has not yet sent on. This
            runs the opposite way to commission: <strong>they owe the house this money</strong>.
            Crediting stops automatically when an agent passes their ceiling or their daily cap.
          </p>
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Agent</th>
                <th scope="col" role="columnheader">Collects into</th>
                <th scope="col" role="columnheader">Holding</th>
                <th scope="col" role="columnheader">Today</th>
                <th scope="col" role="columnheader">Limits</th>
                <th scope="col" role="columnheader">Status</th>
                {manage ? <th scope="col" role="columnheader">Record a remittance</th> : null}
              </tr>
            </thead>
            <tbody role="rowgroup">
              {collections.map((c) => (
                <tr role="row" key={c.agentId}>
                  <td role="cell" data-label="Agent">
                    <a href={href({ name: 'player', id: c.agentId })}>
                      {c.fullName ?? (c.username ? `@${c.username}` : c.agentId.slice(0, 8))}
                    </a>
                  </td>
                  <td role="cell" data-label="Collects into">
                    <code>{c.telebirrAccount}</code>
                    {c.accountName ? <div className="muted">{c.accountName}</div> : null}
                  </td>
                  <td role="cell" data-label="Holding"><strong>{etb(c.outstanding)}</strong></td>
                  <td role="cell" data-label="Today" className="muted">{etb(c.creditedToday)}</td>
                  <td role="cell" data-label="Limits" className="muted">
                    {etb(c.floatCeiling)} ceiling
                    <div>{etb(c.dailyCreditCap)} a day</div>
                  </td>
                  <td role="cell" data-label="Status">
                    <span className={`status status--${c.frozen ? 'rejected' : 'approved'}`}>
                      {c.frozen ? 'frozen' : 'crediting'}
                    </span>
                    {c.frozenReason ? <div className="muted">{c.frozenReason}</div> : null}
                  </td>
                  {manage ? (
                    <td role="cell" data-label="Record a remittance">
                      <input
                        className="input"
                        inputMode="decimal"
                        placeholder="ETB sent"
                        value={remit[c.agentId] ?? ''}
                        onChange={(e) => setRemit({ ...remit, [c.agentId]: e.target.value })}
                      />
                      <ConfirmButton
                        className="button button--primary"
                        confirm={`Record that this agent sent the house ${remit[c.agentId] ?? '—'} ETB? Do this only after it has landed.`}
                        disabled={busy || !(remit[c.agentId] ?? '').trim()}
                        onConfirm={() => void recordRemittance(c)}
                      >
                        Record
                      </ConfirmButton>
                      <ConfirmButton
                        className={`button${c.frozen ? '' : ' button--danger'}`}
                        confirm={
                          c.frozen
                            ? 'Let this agent credit players again? Check what they have collected against their Telebirr statement first.'
                            : 'Stop this agent crediting players? Their deposits will still be recorded, but nobody is credited until this is cleared.'
                        }
                        disabled={busy}
                        onConfirm={() => void toggleFrozen(c)}
                      >
                        {c.frozen ? 'Unfreeze' : 'Freeze'}
                      </ConfirmButton>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {collections && manage ? (
        <div className="card">
          <h2>Set up collection for an agent</h2>
          <p className="muted">
            The agent's players pay into this number, and their forwarder posts to{' '}
            <code>/webhooks/telebirr-sms/agent/&lt;their id&gt;</code> signed with the key below.
            Give each agent their own key — it is the whole authority for "money arrived", so a
            shared one would let any agent credit against any other's float.
          </p>
          <Field label="Agent" hint="Their player id. They must already have an agent rate.">
            <input
              className="input"
              value={collectionForm.agentId}
              onChange={(e) => setCollectionForm({ ...collectionForm, agentId: e.target.value })}
            />
          </Field>
          <Field label="Telebirr number they collect into">
            <input
              className="input"
              placeholder="e.g. 0912345678"
              value={collectionForm.telebirrAccount}
              onChange={(e) =>
                setCollectionForm({ ...collectionForm, telebirrAccount: e.target.value })
              }
            />
          </Field>
          <Field label="Account name" hint="Shown to their players with the number.">
            <input
              className="input"
              value={collectionForm.accountName}
              onChange={(e) => setCollectionForm({ ...collectionForm, accountName: e.target.value })}
            />
          </Field>
          <Field label="Webhook signing key" hint="At least 16 characters. Stored once and never shown again.">
            <input
              className="input"
              type="password"
              autoComplete="off"
              value={collectionForm.smsWebhookSecret}
              onChange={(e) =>
                setCollectionForm({ ...collectionForm, smsWebhookSecret: e.target.value })
              }
            />
          </Field>
          <Field label="Float ceiling (ETB)" hint="The most of the house's money this agent may be holding at once. This is the exposure if they disappear.">
            <input
              className="input"
              inputMode="decimal"
              placeholder="e.g. 5000"
              value={collectionForm.floatCeiling}
              onChange={(e) =>
                setCollectionForm({ ...collectionForm, floatCeiling: e.target.value })
              }
            />
          </Field>
          <Field label="Daily credit cap (ETB)" hint="How much they may credit players in a rolling 24 hours, so the ceiling cannot be reached in one burst.">
            <input
              className="input"
              inputMode="decimal"
              placeholder="e.g. 2000"
              value={collectionForm.dailyCreditCap}
              onChange={(e) =>
                setCollectionForm({ ...collectionForm, dailyCreditCap: e.target.value })
              }
            />
          </Field>
          <ConfirmButton
            className="button button--primary"
            confirm={`Let this agent collect deposits into ${collectionForm.telebirrAccount.trim() || 'that number'}?`}
            disabled={
              busy ||
              !collectionForm.agentId.trim() ||
              !collectionForm.telebirrAccount.trim() ||
              !collectionForm.smsWebhookSecret.trim() ||
              !collectionForm.floatCeiling.trim() ||
              !collectionForm.dailyCreditCap.trim()
            }
            onConfirm={() => void saveCollection()}
          >
            Save collection
          </ConfirmButton>
        </div>
      ) : null}

      {bots && manage ? (
        <div className="card">
          <h2>Agent bots</h2>
          <p className="muted">
            An agent can run the game under their own Telegram bot. Only the storefront is theirs —
            deposits still land in the house Telebirr and withdrawals still go out from it. Give
            the agent this link for their bot's menu button:{' '}
            <code>https://app.arifbingo.de/?a=&lt;slug&gt;</code>
          </p>

          {bots.length === 0 ? (
            <p className="muted">No agent bots yet.</p>
          ) : (
            <table className="table" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Slug</th>
                  <th scope="col" role="columnheader">Bot</th>
                  <th scope="col" role="columnheader">Mini app</th>
                  <th scope="col" role="columnheader">Status</th>
                  <th scope="col" role="columnheader" />
                </tr>
              </thead>
              <tbody role="rowgroup">
                {bots.map((b) => (
                  <tr role="row" key={b.agentId}>
                    <td role="cell" data-label="Slug"><code>{b.slug}</code></td>
                    <td role="cell" data-label="Bot">@{b.botUsername}</td>
                    <td role="cell" data-label="Mini app" className="muted">{b.miniAppName ?? '—'}</td>
                    <td role="cell" data-label="Status">
                      <span className={`status status--${b.active ? 'approved' : 'rejected'}`}>
                        {b.active ? 'active' : 'off'}
                      </span>
                    </td>
                    <td role="cell" data-label="">
                      <ConfirmButton
                        className={`button${b.active ? ' button--danger' : ''}`}
                        confirm={
                          b.active
                            ? 'Switch this bot off? Its players cannot open the app at all until it is switched back on.'
                            : 'Switch this bot back on?'
                        }
                        disabled={busy}
                        onConfirm={() => void toggleBot(b)}
                      >
                        {b.active ? 'Switch off' : 'Switch on'}
                      </ConfirmButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2>Add or update a bot</h2>
          <Field label="Agent" hint="The agent's player id. They must already have an agent rate.">
            <input
              className="input"
              value={form.agentId}
              onChange={(e) => setForm({ ...form, agentId: e.target.value })}
            />
          </Field>
          <Field label="Slug" hint="Lowercase letters, digits and hyphens. Rides in their mini app link.">
            <input
              className="input"
              placeholder="e.g. abebe"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
            />
          </Field>
          <Field label="Bot username">
            <input
              className="input"
              placeholder="e.g. AbebeBingoBot"
              value={form.botUsername}
              onChange={(e) => setForm({ ...form, botUsername: e.target.value })}
            />
          </Field>
          <Field label="Bot token" hint="From @BotFather. Stored once and never shown again.">
            <input
              className="input"
              type="password"
              autoComplete="off"
              value={form.botToken}
              onChange={(e) => setForm({ ...form, botToken: e.target.value })}
            />
          </Field>
          <Field label="Mini app short name" hint="Only if theirs differs from the house one.">
            <input
              className="input"
              value={form.miniAppName}
              onChange={(e) => setForm({ ...form, miniAppName: e.target.value })}
            />
          </Field>
          <ConfirmButton
            className="button button--primary"
            confirm={`Save this bot for agent ${form.agentId.trim() || '(none)'}?`}
            disabled={busy || !form.agentId.trim() || !form.slug.trim() || !form.botToken.trim()}
            onConfirm={() => void saveBot()}
          >
            Save bot
          </ConfirmButton>
        </div>
      ) : null}
    </section>
  );
}
